import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, BookOpen, X,
  ArrowUp, ArrowDown, ArrowsDownUp, DotsSixVertical,
} from "@phosphor-icons/react";

const TYPES = [
  { key: "autoridad", label: "Autoridades", icon: "🏛️" },
  { key: "categoria", label: "Categorías", icon: "📂" },
  { key: "tipo_documental", label: "Tipos documentales", icon: "📄" },
  { key: "tipo_instalacion", label: "Tipos de instalación", icon: "🏭" },
  { key: "frecuencia_notificacion", label: "Frecuencias notificación", icon: "🔔" },
];

export default function Catalogs() {
  const [active, setActive] = useState(TYPES[0].key);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const reload = () => api.get("/catalogs", { params: { type: active } }).then(r => setItems(r.data));
  useEffect(() => { reload(); }, [active]);

  const persistOrder = async (next) => {
    setReorderBusy(true);
    try {
      await api.post("/catalogs/reorder", { ids: next.map(i => i.id) });
      toast.success("Orden actualizado");
      reload();
    } catch { toast.error("Error al reordenar"); reload(); }
    finally { setReorderBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/catalogs/${id}`); toast.success("Eliminado"); reload(); } catch { toast.error("Error"); }
  };

  const moveItem = (idx, dir) => {
    const next = items.slice();
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    persistOrder(next);
  };

  const normalizeIndices = () => persistOrder(items);

  // Drag & drop handlers
  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) {}
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (e, idx) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const next = items.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(null);
    setItems(next);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div className="space-y-6" data-testid="catalogs-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Configuración global</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Catálogos del sistema</h1>
        <p className="text-neutral-500 mt-2">Gestiona los valores reutilizables y su orden en todos los formularios y selectores.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TYPES.map(t => (
          <button key={t.key} onClick={() => setActive(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold rounded-md transition-all ${active===t.key?'bg-blue-600 text-white':'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}
            data-testid={`catalog-tab-${t.key}`}>
            <span className="mr-2">{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold">{TYPES.find(t=>t.key===active)?.label}</h2>
          <div className="flex gap-2">
            <button
              onClick={normalizeIndices}
              disabled={reorderBusy || items.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-neutral-200 bg-white text-neutral-700 font-semibold rounded-md hover:bg-neutral-50 disabled:opacity-50"
              title="Reasigna índices 0..N según el orden actual"
              data-testid="normalize-indices-btn"
            >
              <ArrowsDownUp size={14}/> Normalizar índices
            </button>
            <button onClick={()=>{setEditing(null); setOpen(true);}} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="new-catalog-btn">
              <Plus size={16} weight="bold"/> Agregar
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-widest text-neutral-500">
            <th className="px-3 py-3 w-24">Orden</th>
            <th className="px-5 py-3">Nombre</th>
            <th className="px-5 py-3">Descripción</th>
            <th className="px-5 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {items.map((c, idx) => {
              const isDragging = dragIdx === idx;
              const isDragOver = dragOverIdx === idx && dragIdx !== idx;
              return (
              <tr
                key={c.id}
                draggable
                onDragStart={(e)=>onDragStart(e, idx)}
                onDragOver={(e)=>onDragOver(e, idx)}
                onDragLeave={onDragLeave}
                onDrop={(e)=>onDrop(e, idx)}
                onDragEnd={onDragEnd}
                className={`border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${isDragging?'opacity-40':''} ${isDragOver?'bg-blue-50 outline outline-2 outline-blue-400':''}`}
                data-testid={`catalog-${c.id}`}
              >
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <DotsSixVertical
                      size={16}
                      weight="bold"
                      className="text-neutral-400 cursor-grab active:cursor-grabbing hover:text-blue-600"
                      data-testid={`catalog-drag-${c.id}`}
                      title="Arrastra para reordenar"
                    />
                    <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs font-mono w-7 text-center" data-testid={`catalog-order-${c.id}`}>
                      {c.order_index ?? 0}
                    </span>
                    <div className="flex flex-col">
                      <button
                        disabled={reorderBusy || idx === 0}
                        onClick={() => moveItem(idx, -1)}
                        className="text-neutral-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Subir"
                        data-testid={`catalog-up-${c.id}`}
                      ><ArrowUp size={12} weight="bold"/></button>
                      <button
                        disabled={reorderBusy || idx === items.length-1}
                        onClick={() => moveItem(idx, 1)}
                        className="text-neutral-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Bajar"
                        data-testid={`catalog-down-${c.id}`}
                      ><ArrowDown size={12} weight="bold"/></button>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 font-semibold">{c.name}</td>
                <td className="px-5 py-3 text-neutral-600">{c.description||"—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={()=>{setEditing(c); setOpen(true);}} className="text-blue-600 mr-3" data-testid={`edit-catalog-${c.id}`}><PencilSimple size={16}/></button>
                  <button onClick={()=>onDelete(c.id)} className="text-rose-500" data-testid={`delete-catalog-${c.id}`}><Trash size={16}/></button>
                </td>
              </tr>
              );
            })}
            {items.length===0 && <tr><td colSpan={4} className="py-12 text-center text-neutral-400"><BookOpen size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/><p>Sin entradas. Agrega la primera.</p></td></tr>}
          </tbody>
        </table>
      </div>

      {open && <Form type={active} initial={editing} onClose={()=>setOpen(false)} onSuccess={()=>{setOpen(false); reload();}}/>}
    </div>
  );
}

function Form({ type, initial, onClose, onSuccess }) {
  const [f, setF] = useState(initial || { type, name:"", description:"", order_index: 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name) return toast.error("Nombre requerido");
    setBusy(true);
    try {
      const payload = {
        type,
        name: f.name,
        description: f.description||"",
        order_index: Number(f.order_index) || 0,
      };
      if (initial) await api.put(`/catalogs/${initial.id}`, payload);
      else await api.post("/catalogs", payload);
      toast.success("Guardado"); onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail||"Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-md border border-neutral-200 anim-fade-up">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold">{initial?"Editar":"Nueva"} entrada</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
            <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="catalog-form-name"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description||""} onChange={e=>setF({...f,description:e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">
              Índice de orden
              <span className="ml-2 normal-case font-normal text-neutral-400 text-[10px]">(menor = aparece primero)</span>
            </label>
            <input
              type="number"
              value={f.order_index ?? 0}
              onChange={e=>setF({...f, order_index: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md"
              data-testid="catalog-form-order"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Si dos índices coinciden, el sistema usa el nombre alfabéticamente como criterio secundario.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="catalog-form-submit">{busy?"Guardando...":"Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
